import {
  AfterContentInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  inject,
} from '@angular/core';
import { Observable, Subject, fromEvent, map, startWith, takeUntil } from 'rxjs';
import { ModusPrefix, ModusSuffix } from '../../modus-form-field';

export type ModusListItemsize = 'small' | 'default' | 'large';

export enum ListItemSelectionChangeSource {
  userInput = 'userInput',
  system = 'system',
}

export class ListItemSelectionChange {
  constructor(
    public readonly item: ModusListItem,
    public readonly source: ListItemSelectionChangeSource = ListItemSelectionChangeSource.system,
  ) {}
}

/* eslint-disable @angular-eslint/component-class-suffix */

@Component({
  selector: 'md-list-item',
  templateUrl: './modus-list-item.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ModusListItem implements OnInit, AfterContentInit, OnDestroy {
  private readonly _element = inject(ElementRef).nativeElement;
  private readonly _changeDetectorRef = inject(ChangeDetectorRef);

  private readonly _destroy$ = new Subject<void>();
  private _size: ModusListItemsize = 'default';
  private _selected = false;

  @ContentChildren(ModusPrefix, { descendants: true })
  private _prefixChildren!: QueryList<ModusPrefix>;

  @ContentChildren(ModusSuffix, { descendants: true })
  private _suffixChildren!: QueryList<ModusSuffix>;

  @Input()
  set size(value: ModusListItemsize) {
    this._size = value;
  }

  @Output() readonly selectionChange = new EventEmitter<ListItemSelectionChange>();

  @HostBinding('class.modus-list-item') listItemClass = true;

  @HostBinding('class')
  get sizeClass() {
    return this._size !== 'default' ? `modus-list-item-${this._size}` : '';
  }

  @HostBinding('class.modus-list-item-selected')
  get selected(): boolean {
    return this._selected;
  }

  hasPrefix$!: Observable<boolean>;
  hasSuffix$!: Observable<boolean>;

  ngOnInit(): void {
    fromEvent<MouseEvent>(this._element, 'click')
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: () => this.select(ListItemSelectionChangeSource.userInput),
      });
  }

  ngAfterContentInit(): void {
    this.hasPrefix$ = this._prefixChildren.changes.pipe(
      map((queryList: QueryList<ModusPrefix>) => queryList.length > 0),
      startWith(this._prefixChildren.length > 0),
      takeUntil(this._destroy$),
    );

    this.hasSuffix$ = this._suffixChildren.changes.pipe(
      map((queryList: QueryList<ModusSuffix>) => queryList.length > 0),
      startWith(this._suffixChildren.length > 0),
      takeUntil(this._destroy$),
    );
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  select(source: ListItemSelectionChangeSource = ListItemSelectionChangeSource.system) {
    this._selected = true;
    this._changeDetectorRef.markForCheck();
    this.selectionChange.emit(new ListItemSelectionChange(this, source));
  }

  deselect(source: ListItemSelectionChangeSource = ListItemSelectionChangeSource.system) {
    this._selected = false;
    this._changeDetectorRef.markForCheck();
    this.selectionChange.emit(new ListItemSelectionChange(this, source));
  }
}
